/**
 * Searchspring Results List Component
 *
 * A custom element that fetches products from Searchspring and renders
 * rich product cards via batch Section Rendering API.
 *
 * Flow:
 * 1. Skeleton tiles are visible (server-rendered)
 * 2. Searchspring API returns product handles
 * 3. Batch section rendering fetches rich Liquid cards (3 requests for up to 60 products)
 * 4. Single DOM swap replaces skeletons with rich cards (no visual flash)
 * 5. Falls back to basic client-side cards if batch fails
 *
 * Features:
 * - Searchspring-powered product fetching with background filters
 * - Batch section rendering (2 requests max, down from 24)
 * - LCP image preloading for above-fold products
 * - Skeleton-to-rich card swap (no visual flash)
 * - Client-side fallback cards if batch rendering fails
 * - URL state management for filters, sort, and pagination
 * - SEO-friendly pagination
 */

/* global SearchspringAPI */

import { Component } from '@theme/component';
import { mediaQueryLarge, isIOSDevice } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import {
  initCollectionImageObserver,
  observeImages,
  destroyCollectionImageObserver,
} from '@theme/collection-image-observer';

/** Number of product tiles above the fold to eager-load (preload + high priority). */
const ABOVE_FOLD_COUNT = 4;

/**
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLElement} [loadingOverlay] - Loading overlay element.
 * @property {HTMLElement} [pagination] - Pagination container.
 * @property {HTMLElement} [facetsContainer] - Facets container.
 * @property {HTMLElement} [activeFacets] - Active facets bar.
 * @property {HTMLElement} [productCount] - Product count display.
 * @property {HTMLElement[]} [cards] - The product card elements.
 *
 * @extends Component<Refs>
 */
class SearchspringResultsList extends Component {
  // Private fields for web component best practices
  /** @type {AbortController | null} - Component lifecycle controller (aborted on disconnect) */
  #abortController = null;
  /** @type {AbortController | null} - Per-request controller; aborted when a newer request starts */
  #requestController = null;
  /** Monotonically-increasing counter to identify the latest request and ignore stale ones */
  #requestGeneration = 0;
  /** @type {AbortController | null} - Per-prefetch controller; aborted when a new hover target is entered */
  #prefetchController = null;

  /**
   * Searchspring configuration
   */
  get config() {
    return window.searchspringConfig || {};
  }

  /**
   * Check if Searchspring is enabled
   */
  get isEnabled() {
    return typeof SearchspringAPI !== 'undefined' && SearchspringAPI?.isEnabled();
  }

  /**
   * Get collection handle from data attribute
   */
  get collectionHandle() {
    return this.dataset.collectionHandle || '';
  }

  /**
   * True when the component is used on the search page (data-context="search").
   */
  get isSearchMode() {
    return this.dataset.context === 'search';
  }

  /**
   * Search query: URL param `q` (source of truth after navigation) or fallback to data-search-query for initial load.
   */
  get searchQuery() {
    const q = new URLSearchParams(window.location.search).get('q');
    return q != null && q !== '' ? q : this.dataset.searchQuery || '';
  }

  /**
   * Get section ID
   */
  get sectionId() {
    return this.dataset.sectionId || 'collection';
  }

  /**
   * Get current page from URL
   */
  get currentPage() {
    const urlParams = new URLSearchParams(window.location.search);
    return parseInt(urlParams.get('page') || '1', 10);
  }

  /**
   * Get current sort from URL
   */
  get currentSort() {
    const urlParams = new URLSearchParams(window.location.search);
    const sortKey = urlParams.get('sort_by');
    if (!sortKey) return null;

    const [field, direction = 'asc'] = sortKey.split('-');
    return { field, direction };
  }

  /**
   * Get active filters from URL
   * @returns {string[]}
   */
  get activeFilters() {
    const urlParams = new URLSearchParams(window.location.search);
    /** @type {string[]} */
    const filters = [];

    urlParams.forEach((value, key) => {
      if (key.startsWith('filter.') || key.startsWith('bgfilter.')) {
        filters.push(`${key}=${encodeURIComponent(value)}`);
      }
    });

    return filters;
  }

  connectedCallback() {
    super.connectedCallback();

    if (!this.isEnabled) {
      console.warn('[Searchspring] Not enabled, using native pagination');
      return;
    }

    // Initialize AbortController for cleanup
    const controller = new AbortController();
    this.#abortController = controller;
    const { signal } = controller;

    // Listen for filter updates
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate, { signal });

    // Listen for popstate (browser back/forward)
    window.addEventListener('popstate', this.#handlePopState, { signal });

    // Listen for media query changes
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });

    // Listen for grid density changes (controls may be in filter bar section, outside this component)
    document.addEventListener('change', this.#handleGridDensityChange, { signal });

    // Listen for mobile toggle clicks (button may be in filter bar section, outside this component)
    document.addEventListener('click', this.#handleGridToggleClick, { signal });

    // On iOS Safari, bfcache restore re-fires connectedCallback after
    // disconnectedCallback already aborted the previous signal.
    // Event listeners above must be re-attached, but skip the fetch —
    // the bfcache-restored DOM already has the correct cards.
    if (this.hasAttribute('ss-initialized')) {
      return;
    }

    // Initial fetch - skeletons are already rendered server-side
    this.#fetchProducts();

    // Activate iOS image memory observer to cap decoded image textures
    initCollectionImageObserver();

    this.setAttribute('ss-initialized', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    destroyCollectionImageObserver();

    // Cancel pending requests and cleanup
    if (this.#abortController) {
      this.#abortController.abort();
      this.#abortController = null;
    }
  }

  /**
   * Abort the previous in-flight request (if any) and return a fresh AbortSignal.
   * @returns {AbortSignal}
   */
  #getAbortSignal() {
    if (this.#requestController) {
      this.#requestController.abort();
    }
    this.#requestController = new AbortController();
    return this.#requestController.signal;
  }

  /**
   * Fetch products from Searchspring
   * @param {Object} [options] - Fetch options
   * @param {boolean} [options.updateUrl=false] - Whether to update URL
   * @param {boolean} [options.skipRedirect=false] - When true, do not follow API redirect (keeps SPA behavior for filter/sort/page)
   */
  async #fetchProducts(options = {}) {
    const { updateUrl = false, skipRedirect = false } = options;

    // Stamp this invocation. Any previously-running fetch will see its generation
    // is stale and exit without touching the DOM or loading state.
    const generation = ++this.#requestGeneration;

    this.#showLoading();
    const signal = this.#getAbortSignal();

    try {
      // Fetch product data from Searchspring
      if (!SearchspringAPI) throw new Error('SearchspringAPI not loaded');
      const searchOptions = {
        page: this.currentPage,
        resultsPerPage: this.config.resultsPerPage,
        filters: this.activeFilters,
        sort: this.currentSort,
        ...(this.isSearchMode
          ? { query: this.searchQuery }
          : { collectionHandle: this.collectionHandle }),
      };
      const response = await SearchspringAPI.search(searchOptions);

      if (signal.aborted) return;

      if (!response) {
        throw new Error('No response from Searchspring');
      }

      // Handle redirects (skip when updating from filter/sort/page to avoid full reload)
      const rawResponse = /** @type {SearchspringRawResponse} */ (response);
      if (!skipRedirect && rawResponse.redirected && rawResponse.redirectUrl) {
        window.location.href = rawResponse.redirectUrl;
        return;
      }

      const formattedResponse = SearchspringAPI.formatSearchResponse(response);
      if (!formattedResponse) {
        throw new Error('Failed to format Searchspring response');
      }

      // Update drawer (facets + applied filters) and product count as soon as API returns
      // so the UI feels responsive before batch card requests complete
      this.#dispatchFacetAndCountUpdates(formattedResponse);

      // Start image preloading while waiting for batch
      this.#preloadImages(formattedResponse.products);

      // Fetch rich cards via batch section rendering (2 requests max)
      const handles = formattedResponse.products.map((p) => p.handle).filter(Boolean);
      const cardMap = await this.#fetchBatchCards(handles, signal);

      if (signal.aborted) return;

      // Render: rich cards if batch succeeded, basic cards as fallback
      await this.#renderResults(formattedResponse, cardMap);

      // Register new card images with the iOS memory observer
      observeImages(this);

      // Update URL if needed
      if (updateUrl) {
        this.#updateUrl();
      }

      // Dispatch event for analytics
      this.#dispatchResultsEvent(formattedResponse);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      if (generation !== this.#requestGeneration) return;
      console.error('[Searchspring] Failed to fetch products:', error);
      this.#showError();
    } finally {
      // Only the latest request should clear the loading state.
      if (generation === this.#requestGeneration) {
        this.#hideLoading();
      }
    }
  }

  /**
   * Preload above-fold product images for faster LCP.
   * Injects <link rel="preload"> for the first few product images
   * immediately after API response, before DOM rendering begins.
   * @param {SearchspringProduct[]} products - Formatted product objects
   */
  #preloadImages(products) {
    const toPreload = products.slice(0, ABOVE_FOLD_COUNT);

    toPreload.forEach((product) => {
      if (!product.imageUrl) return;

      // Avoid duplicate preloads
      const existing = document.querySelector(`link[rel="preload"][href="${product.imageUrl}"]`);
      if (existing) return;

      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = product.imageUrl;
      link.setAttribute('fetchpriority', 'high');
      document.head.appendChild(link);
    });
  }

  /**
   * Dispatch facet, active-facets, and product count updates as soon as Searchspring API returns.
   * This lets the drawer (facets + applied filters) and count update before batch card requests finish.
   * @param {SearchspringFormattedResponse} response - Formatted Searchspring response
   */
  #dispatchFacetAndCountUpdates(response) {
    const { productCount } = this.refs;

    if (productCount && response.pagination != null) {
      const count = response.pagination.totalResults ?? 0;
      const one =
        /** @type {string} */ (Theme?.translations?.content_item_count_one) ?? '{{ count }} item';
      const other =
        /** @type {string} */ (Theme?.translations?.content_item_count_other) ??
        '{{ count }} items';
      const template = count === 1 ? one : other;
      productCount.textContent = String(template).replace(/\{\{\s*count\s*\}\}/g, String(count));
    }

    if (this.isSearchMode && response.pagination != null) {
      const count = response.pagination.totalResults ?? 0;
      const countStr = String(count);
      const countEl = document.getElementById('search-results-count');
      if (countEl) countEl.textContent = countStr;
    }
    if (response.pagination != null) {
      const count = response.pagination.totalResults ?? 0;
      const filterBarCountEl = document.getElementById('searchspring-filter-bar-count');
      if (filterBarCountEl) {
        const tpl = filterBarCountEl.dataset.template;
        const formatted = tpl ? tpl.replace('__COUNT__', String(count)) : String(count);
        filterBarCountEl.textContent = formatted;
        document.querySelectorAll('.searchspring-filter-bar-count--mobile').forEach((el) => {
          el.textContent = formatted;
        });
      }
    }

    const totalResults = response.pagination?.totalResults ?? response.products?.length ?? 0;
    const facetsToggle = document.getElementById('searchspring-facets-toggle');
    if (facetsToggle) facetsToggle.hidden = totalResults === 0;

    if (response.facets) {
      document.dispatchEvent(
        new CustomEvent('ss:facets-update', {
          bubbles: true,
          detail: { facets: response.facets },
        }),
      );
    }

    document.dispatchEvent(
      new CustomEvent('ss:active-facets-update', {
        bubbles: true,
        detail: { filterSummary: response.filterSummary },
      }),
    );

    document.dispatchEvent(
      new CustomEvent('ss:results-loaded', {
        bubbles: true,
        detail: { facets: response.facets, pagination: response.pagination },
      }),
    );

    document.dispatchEvent(
      new CustomEvent('ss:sorting-update', {
        bubbles: true,
        detail: {
          options: response.sorting?.options || [],
          current: response.sorting?.current,
          totalResults: response.pagination?.totalResults ?? 0,
        },
      }),
    );
  }

  /**
   * Fetch rich product cards via batch Section Rendering API.
   * Splits handles into chunks of 20 (all_products limit) and makes parallel requests.
   * Returns a Map of handle -> HTML string for each successfully rendered card.
   *
   * Results are cached in sessionStorage keyed by the sorted handle list.
   * Rendered card HTML is stable within a session (product data doesn't change),
   * so cache hits avoid the Shopify section-rendering round-trip entirely.
   *
   * @param {string[]} handles - Product handles to fetch
   * @param {AbortSignal} [signal] - Abort signal to cancel in-flight requests
   * @returns {Promise<Map<string, string>>} Map of handle to rich card HTML
   */
  async #fetchBatchCards(handles, signal) {
    const BATCH_SIZE = 20;
    const SECTION_ID = 'searchspring-batch-cards';
    const CACHE_PREFIX = 'ss-batch-cards-';
    const CACHE_DURATION_MS = 300000; // 5 minutes — matches searchspring.js TTL
    const cardMap = new Map();

    if (!handles.length) return cardMap;

    // Stable cache key: sorted handles + locale/currency so switching country
    // invalidates the cache (prices and URLs are locale-dependent).
    const { locale = '', currency = '' } = this.config;
    const cacheKey = CACHE_PREFIX + locale + '-' + currency + '-' + [...handles].sort().join(',');

    try {
      const raw = sessionStorage.getItem(cacheKey);
      if (raw) {
        const { data, expiry } = JSON.parse(raw);
        if (Date.now() < expiry) {
          /** @type {[string, string][]} */
          (data).forEach(([handle, html]) => cardMap.set(handle, html));
          return cardMap;
        }
        sessionStorage.removeItem(cacheKey);
      }
    } catch {
      // Corrupted entry — fall through to network fetch
    }

    // Split handles into chunks of 20 (all_products limit per request)
    const chunks = [];
    for (let i = 0; i < handles.length; i += BATCH_SIZE) {
      chunks.push(handles.slice(i, i + BATCH_SIZE));
    }

    try {
      // Fire all chunk requests in parallel via /search route.
      // Handles are passed as comma-separated values in the `q` parameter,
      // accessible in Liquid via `search.terms`.
      // Use the localized search route from config so locale prefix (e.g. /en-au/) is preserved.
      const searchRoute = this.config?.routes?.search ?? '/search';
      const responses = await Promise.all(
        chunks.map((chunk) => {
          const params = new URLSearchParams({
            section_id: SECTION_ID,
            type: 'product',
            q: chunk.join(','),
          });
          return fetch(`${searchRoute}?${params.toString()}`, { signal }).then((res) => {
            if (!res.ok) throw new Error(`Batch fetch failed: ${res.status}`);
            return res.text();
          });
        }),
      );

      // Parse each response and extract cards by handle
      const parser = new DOMParser();
      responses.forEach((html) => {
        const doc = parser.parseFromString(html, 'text/html');
        const templates = doc.querySelectorAll('template[data-ss-handle]');
        templates.forEach((templateEl) => {
          const template = /** @type {HTMLTemplateElement} */ (templateEl);
          const handle = template.dataset?.ssHandle;
          if (!handle) return;
          // template.content gives us the DocumentFragment; serialize to HTML string
          const div = document.createElement('div');
          div.appendChild(template.content.cloneNode(true));
          cardMap.set(handle, div.innerHTML);
        });
      });

      // Persist to session cache so re-renders of this page skip the network.
      // Guard against quota errors when card HTML is large (e.g. 60 products).
      try {
        const cacheValue = JSON.stringify({
          data: [...cardMap.entries()],
          expiry: Date.now() + CACHE_DURATION_MS,
        });
        if (cacheValue.length < 2_000_000) {
          sessionStorage.setItem(cacheKey, cacheValue);
        }
      } catch {
        // Storage full or disabled — fail silently
      }
    } catch (error) {
      // Re-throw so #fetchProducts can handle it cleanly and skip stale renders
      if (error instanceof Error && error.name === 'AbortError') throw error;
      console.warn('[Searchspring] Batch card fetch failed, will use fallback cards:', error);
      // Return empty map — #renderResults will fall back to basic cards
    }

    return cardMap;
  }

  /**
   * Build a Shopify CDN image URL with a specific width.
   * Handles both URLs with existing width params and those without.
   * @param {string} src - Base image URL
   * @param {number} width - Desired width in pixels
   * @returns {string} URL with width parameter
   */
  #buildImageUrl(src, width) {
    if (!src) return '';
    // Remove existing width/height params
    let cleanSrc = src
      .replace(/([?&])width=\d+/g, '$1')
      .replace(/([?&])height=\d+/g, '$1')
      .replace(/[?&]+$/, '')
      .replace(/\?&/g, '?')
      .replace(/&&+/g, '&');

    const separator = cleanSrc.includes('?') ? '&' : '?';
    return `${cleanSrc}${separator}width=${width}`;
  }

  /**
   * Build a responsive srcset string for a product image.
   * @param {string} src - Base image URL
   * @returns {string} srcset attribute value
   */
  #buildSrcset(src) {
    if (!src) return '';
    const widths = [240, 278, 322, 374, 434, 503, 583, 676, 784, 909, 1054, 1223, 1419, 1593];
    return widths.map((w) => `${this.#buildImageUrl(src, w)} ${w}w`).join(', ');
  }

  /**
   * Escape HTML special characters to prevent XSS
   * @param {string} str - String to escape
   * @returns {string} Escaped string
   */
  #escapeHtml(str) {
    if (!str) return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /**
   * Render search results — uses rich Liquid cards from batch if available,
   * otherwise falls back to basic client-side cards.
   * @param {SearchspringFormattedResponse} response - Formatted Searchspring response
   * @param {Map<string, string>} [cardMap] - Map of handle -> rich card HTML from batch rendering
   */
  async #renderResults(response, cardMap) {
    const { grid, pagination } = this.refs;

    if (!grid) return;

    // Product count and facet events were already dispatched in #dispatchFacetAndCountUpdates

    // Render products
    if (response.products.length > 0) {
      const useBatchCards = cardMap && cardMap.size > 0;

      // Build grid HTML: rich cards from batch, or fallback basic cards
      grid.innerHTML = response.products
        .map((/** @type {SearchspringProduct} */ product, /** @type {number} */ index) => {
          const richHtml = useBatchCards ? cardMap.get(product.handle) : null;

          if (richHtml) {
            // Wrap rich Liquid card in the grid item <li>
            return `
              <li
                id="${this.sectionId}-${product.id}"
                class="product-grid__item product-grid__item--${index}"
                data-page="${this.currentPage}"
                data-product-id="${product.id}"
                data-product-handle="${product.handle}"
                ref="cards[]"
              >
                ${richHtml}
              </li>
            `;
          }

          // Fallback: basic client-side card
          return this.#renderProductCard(product, index);
        })
        .join('');

      // Initialize any custom elements that were just added
      this.#initializeCardComponents(grid);

      // Dispatch event for other components (analytics, swatches, etc.)
      this.dispatchEvent(
        new CustomEvent('ss:products-rendered', {
          bubbles: true,
          detail: { products: response.products, count: response.products.length },
        }),
      );
    } else {
      const trendingHtml = await this.#fetchAndBuildTrendingHtml();
      const noProductsFound =
        /** @type {string} */ (Theme?.translations?.content_no_products_found) ??
        'No products found.';
      const fallback =
        trendingHtml ||
        `<div class="ss-no-results__content"><p class="ss-no-results__title">${this.#escapeHtml(noProductsFound)}</p></div>`;
      grid.innerHTML = `<li class="ss-no-results">${fallback}</li>`;
    }

    // Update pagination: hide .ss-pagination-container when 0 or 1 page, show and fill when 2+
    // (ref "pagination" can point to inner snippet div when duplicate refs exist, so target container by class)
    const paginationContainer = /** @type {HTMLElement|null} */ (
      this.querySelector('.ss-pagination-container')
    );
    if (paginationContainer) {
      const totalPages = response.pagination?.totalPages ?? 0;
      if (totalPages <= 1) {
        paginationContainer.hidden = true;
        if (pagination) pagination.innerHTML = '';
      } else {
        paginationContainer.hidden = false;
        if (pagination && response.pagination)
          pagination.innerHTML = this.#renderPagination(response.pagination);
      }
    }

    // Update grid data attributes
    if (response.pagination) {
      grid.dataset.lastPage = String(response.pagination.totalPages);
      grid.dataset.currentPage = String(response.pagination.currentPage);
      grid.dataset.totalResults = String(response.pagination.totalResults);
    }
  }

  /**
   * Initialize JavaScript components in newly rendered product cards
   * This ensures swatches, quick-add, and other custom elements are properly initialized
   * @param {HTMLElement} container - The container with new cards
   */
  #initializeCardComponents(container) {
    // Custom elements (product-card, quick-add-component, etc.) auto-upgrade when
    // connected to the document.
    // Dispatch event for swatch initialization and other components
    // The product-card-swatches.js listens for this event
    container.dispatchEvent(
      new CustomEvent('ss:cards-initialized', {
        bubbles: true,
        detail: { container },
      }),
    );
  }

  /**
   * Fetch trending terms from Searchspring and build HTML for no-results state (search page only).
   * @returns {Promise<string>} HTML string for trending block, or empty string if none
   */
  async #fetchAndBuildTrendingHtml() {
    try {
      const data =
        typeof SearchspringAPI !== 'undefined' && SearchspringAPI?.getTrending
          ? await SearchspringAPI.getTrending()
          : null;
      const raw = data?.trending?.queries;
      if (!raw?.length) return '';

      const terms = raw
        .slice(0, 9)
        .map((/** @type {string | { query?: string, searchQuery?: string, term?: string }} */ q) =>
          typeof q === 'string' ? q : (q?.query ?? q?.searchQuery ?? q?.term ?? String(q)),
        );
      const title =
        /** @type {string} */ (Theme?.translations?.content_ss_popular_searches) ??
        Theme?.translations?.ss_popular_searches ??
        'Popular searches';
      return typeof SearchspringAPI !== 'undefined' && SearchspringAPI?.buildTrendingHtml
        ? SearchspringAPI.buildTrendingHtml(terms, { title, variant: 'no-results' })
        : '';
    } catch (error) {
      console.warn('[Searchspring] Failed to load trending terms for no-results:', error);
      return '';
    }
  }

  /**
   * Render a product card using client-side template from Searchspring API data.
   * Uses native product card classes to inherit existing theme styling.
   * Adds fetchpriority="high" for above-fold images to improve LCP.
   * Title displays in full (no truncation).
   * @param {SearchspringProduct} product - Formatted product data from Searchspring
   * @param {number} index - Product index in the grid
   * @returns {string} HTML string
   */
  #renderProductCard(product, index) {
    const onSale = product.compareAtPrice && product.compareAtPrice > product.price;
    const isAboveFold = index < ABOVE_FOLD_COUNT;
    const title = this.#escapeHtml(product.title);
    const srcset = product.imageUrl ? this.#buildSrcset(product.imageUrl) : '';

    return `
      <li
        id="${this.sectionId}-${product.id}"
        class="product-grid__item product-grid__item--${index}"
        data-page="${this.currentPage}"
        data-product-id="${product.id}"
        data-product-handle="${product.handle}"
        ref="cards[]"
      >
        <product-card class="product-card" data-product-id="${product.id}" data-product-handle="${product.handle}">
          <a href="${product.url}" class="product-card__link">
            <span class="visually-hidden">${title}</span>
          </a>
          <div class="product-card__content product-grid__card layout-panel-flex layout-panel-flex--column spacing-style border-style gap-style" style="--product-card-gap: 4px;">
            <div class="card-gallery spacing-style border-style" style="--gallery-aspect-ratio: 4 / 5;" data-product-id="${product.id}" data-image-ratio="portrait">
              <a class="contents" href="${product.url}" aria-label="${title}">
                ${
                  product.imageUrl
                    ? `
                  <img
                    src="${product.imageUrl}"
                    ${srcset ? `srcset="${srcset}"` : ''}
                    sizes="(min-width: 1024px) 25vw, 50vw"
                    alt="${title}"
                    loading="${isAboveFold ? 'eager' : 'lazy'}"
                    decoding="async"
                    ${isAboveFold ? 'fetchpriority="high"' : ''}
                    width="400"
                    height="500"
                    class="product-media__image product-media-container media-fit"
                  />
                `
                    : `
                  <div class="product-card__placeholder-image">
                    <span class="visually-hidden">${title}</span>
                  </div>
                `
                }
              </a>
              ${!product.available ? `<span class="product-card-badge product-card-badge--sold-out">${this.#escapeHtml(/** @type {string} */ (Theme?.translations?.content_product_badge_sold_out) ?? 'Sold out')}</span>` : ''}
              ${onSale ? `<span class="product-card-badge product-card-badge--sale">${this.#escapeHtml(/** @type {string} */ (Theme?.translations?.content_product_badge_sale) ?? 'Sale')}</span>` : ''}
            </div>
            <div class="product-card__title">
              <a href="${product.url}" class="contents">
                <h2 class="product-card__title-text h3">${title}</h2>
              </a>
            </div>
            <product-price class="product-card__price">
              <div class="price">
                ${
                  onSale
                    ? `
                  <span class="price__compare">${product.formattedCompareAtPrice}</span>
                  <span class="price__sale">${product.formattedPrice}</span>
                `
                    : `
                  <span class="price__regular">${product.formattedPrice}</span>
                `
                }
              </div>
            </product-price>
          </div>
        </product-card>
      </li>
    `;
  }

  /**
   * Render pagination using native pagination-controls structure
   * Uses native CSS classes for consistent styling
   * @param {{ currentPage: number, totalPages: number, previousPage: number|null, nextPage: number|null }} pagination - Pagination data from Searchspring
   * @returns {string} HTML string matching native pagination-controls structure
   */
  #renderPagination(pagination) {
    if (!pagination) return '';
    const { currentPage, totalPages, previousPage, nextPage } = pagination;

    if (totalPages <= 1) return '';

    const baseUrl = new URL(window.location.href);

    // Build page URL
    /** @param {number} page */
    const getPageUrl = (page) => {
      const url = new URL(baseUrl);
      if (page === 1) {
        url.searchParams.delete('page');
      } else {
        url.searchParams.set('page', page.toString());
      }
      return url.toString();
    };

    // Calculate visible page numbers (matching native behavior)
    const getVisiblePages = () => {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
      const end = Math.min(totalPages, start + maxVisible - 1);

      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    };

    const visiblePages = getVisiblePages();
    const firstPage = visiblePages[0];
    const lastPage = visiblePages[visiblePages.length - 1];

    // Build page items
    let pageItems = '';

    // Show first page and ellipsis if needed
    if (firstPage !== undefined && firstPage > 1) {
      pageItems += `
        <li class="pagination__item">
          <a href="${getPageUrl(1)}" class="pagination__link pagination__link--page" data-ss-page="1">1</a>
        </li>`;
      if (firstPage > 2) {
        pageItems += `
        <li class="pagination__item pagination__item--gap">
          <span class="pagination__link pagination__link--gap" aria-hidden="true">&hellip;</span>
        </li>`;
      }
    }

    // Visible page numbers — track last mobile-visible page to insert ellipsis for gaps
    let lastMobileVisiblePage = firstPage !== undefined && firstPage > 1 ? 1 : 0;

    visiblePages.forEach((/** @type {number} */ page) => {
      const distanceFromCurrent = Math.abs(page - currentPage);
      const shouldHideOnMobile = page !== 1 && page !== totalPages && distanceFromCurrent > 1;
      const itemClass = shouldHideOnMobile
        ? 'pagination__item pagination__item--mobile-hide'
        : 'pagination__item';

      // Insert mobile-only ellipsis when there's a gap between mobile-visible pages
      if (!shouldHideOnMobile && lastMobileVisiblePage > 0 && page > lastMobileVisiblePage + 1) {
        pageItems += `
        <li class="pagination__item pagination__item--mobile-only pagination__item--gap">
          <span class="pagination__link pagination__link--gap" aria-hidden="true">&hellip;</span>
        </li>`;
      }

      if (!shouldHideOnMobile) {
        lastMobileVisiblePage = page;
      }

      if (page === currentPage) {
        pageItems += `
        <li class="${itemClass}">
          <span class="pagination__link pagination__link--page pagination__link--current" aria-current="page">${page}</span>
        </li>`;
      } else {
        pageItems += `
        <li class="${itemClass}">
          <a href="${getPageUrl(page)}" class="pagination__link pagination__link--page" data-ss-page="${page}">${page}</a>
        </li>`;
      }
    });

    // Show ellipsis and last page if needed
    if (lastPage !== undefined && lastPage < totalPages) {
      if (lastPage < totalPages - 1) {
        // Desktop ellipsis covers both viewports
        pageItems += `
        <li class="pagination__item pagination__item--gap">
          <span class="pagination__link pagination__link--gap" aria-hidden="true">&hellip;</span>
        </li>`;
      } else if (lastMobileVisiblePage > 0 && totalPages > lastMobileVisiblePage + 1) {
        // No desktop ellipsis, but mobile-hidden pages create a gap
        pageItems += `
        <li class="pagination__item pagination__item--mobile-only pagination__item--gap">
          <span class="pagination__link pagination__link--gap" aria-hidden="true">&hellip;</span>
        </li>`;
      }
      pageItems += `
        <li class="pagination__item">
          <a href="${getPageUrl(totalPages)}" class="pagination__link pagination__link--page" data-ss-page="${totalPages}">${totalPages}</a>
        </li>`;
    }

    const backToTopLabel =
      /** @type {string} */ (Theme?.translations?.accessibility_back_to_top) ?? 'Back to top';
    const previousLabel =
      /** @type {string} */ (Theme?.translations?.pagination_previous) ?? 'Previous';
    const nextLabel = /** @type {string} */ (Theme?.translations?.pagination_next) ?? 'Next';
    const chevronUp =
      '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="10" viewBox="0 0 16 10" fill="none" aria-hidden="true"><path d="M0.364746 8.73145L7.86475 0.731445L15.3647 8.73145" stroke="currentColor"/></svg>';

    // Build complete pagination using native structure (back-to-top + nav, matching Liquid snippet)
    return `
      <back-to-top>
        <button aria-label="${this.#escapeHtml(backToTopLabel)}">
          ${chevronUp}
          <span class="back-to-top__text">${this.#escapeHtml(backToTopLabel)}</span>
        </button>
      </back-to-top>
      <div class="pagination-wrapper">
        <nav class="pagination" aria-label="Pagination" data-current_page="${currentPage}">
          <ul class="pagination__list" role="list">
            <li class="pagination__item pagination__item--text">
              ${
                previousPage
                  ? `<a href="${getPageUrl(previousPage)}" aria-label="${this.#escapeHtml(previousLabel)}" class="pagination__link pagination__link--text" data-ss-page="${previousPage}">
                    ${this.#escapeHtml(previousLabel)}
                  </a>`
                  : `<span aria-label="${this.#escapeHtml(previousLabel)}" class="pagination__link pagination__link--text pagination__link--disabled" aria-disabled="true">
                    ${this.#escapeHtml(previousLabel)}
                  </span>`
              }
            </li>
            ${pageItems}
            <li class="pagination__item pagination__item--text">
              ${
                nextPage
                  ? `<a href="${getPageUrl(nextPage)}" aria-label="${this.#escapeHtml(nextLabel)}" class="pagination__link pagination__link--text" data-ss-page="${nextPage}">
                    ${this.#escapeHtml(nextLabel)}
                  </a>`
                  : `<span aria-label="${this.#escapeHtml(nextLabel)}" class="pagination__link pagination__link--text pagination__link--disabled" aria-disabled="true">
                    ${this.#escapeHtml(nextLabel)}
                  </span>`
              }
            </li>
          </ul>
        </nav>
      </div>
    `;
  }

  /**
   * Update URL with current state
   */
  #updateUrl() {
    const url = new URL(window.location.href);

    if (this.currentPage > 1) {
      url.searchParams.set('page', this.currentPage.toString());
    } else {
      url.searchParams.delete('page');
    }

    history.pushState({}, '', url.toString());
  }

  /**
   * Show loading state
   */
  #showLoading() {
    const { loadingOverlay, grid } = this.refs;

    if (loadingOverlay) {
      loadingOverlay.removeAttribute('hidden');
    }

    if (grid) {
      grid.classList.add('ss-loading');
    }

    this.setAttribute('loading', '');
  }

  /**
   * Hide loading state
   */
  #hideLoading() {
    const { loadingOverlay, grid } = this.refs;

    if (loadingOverlay) {
      loadingOverlay.setAttribute('hidden', '');
    }

    if (grid) {
      grid.classList.remove('ss-loading');
    }

    this.removeAttribute('loading');
  }

  /**
   * Show error state
   */
  #showError() {
    const { grid } = this.refs;

    if (grid) {
      grid.innerHTML = `
        <li class="ss-error">
          <div class="ss-error__content">
            <p>Something went wrong loading products.</p>
            <button class="button button-secondary" type="button" onclick="location.reload()">
              Try again
            </button>
          </div>
        </li>
      `;
    }
  }

  /**
   * Handle filter updates
   */
  #handleFilterUpdate = () => {
    this.#scrollToFilterBar();
    this.#fetchProducts({ updateUrl: true, skipRedirect: true });
  };

  /** @type {(() => void) | null} Active header-lock cleanup function */
  #headerLockRelease = null;

  /**
   * Scroll the page so the filter bar button sits at the top of the viewport,
   * just below the sticky header. Skips when the filters drawer is open (the
   * drawer's dialog:close handler handles that case).
   */
  #scrollToFilterBar() {
    const drawerOpen = document.querySelector('#filters-drawer dialog[open]') != null;
    if (drawerOpen) return;

    // Target the original button inside the filter bar section, not the
    // cloned copy inside the sticky bar (both share the same id).
    const btn = document.querySelector('#searchspring-facets-toggle #searchspring-filter-bar-button');
    if (!btn) return;

    // Scroll so the button is flush at the top of the viewport.
    // Set scrollTop directly — bypasses CSS scroll-behavior:smooth entirely,
    // works reliably across all browsers.
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    // On mobile, anchor to text filters if present so they remain visible
    const textFilters = isMobile
      ? document.querySelector('.facets-toggle--searchspring-bar .featured-filters-text-filters')
      : null;
    const anchor = textFilters || btn;
    const top = anchor.getBoundingClientRect().top + window.scrollY - (isMobile ? 70 : 20);
    document.documentElement.scrollTop = Math.max(0, top);

    // Clean up any previous header lock before creating a new one
    if (this.#headerLockRelease) this.#headerLockRelease();

    // Keep the sticky header hidden. The header's own scroll handler runs
    // on the same element (document) and overrides dataset changes. Use a
    // MutationObserver to immediately revert any state change back to
    // 'minimised' until the user reaches the top or the timeout expires.
    const headerComponent = document.getElementById('header-component');
    if (headerComponent) {
      let released = false;
      const release = () => {
        if (released) return;
        released = true;
        this.#headerLockRelease = null;
        observer.disconnect();
        document.removeEventListener('scroll', checkTop);
        headerComponent.dataset.stickyState = window.scrollY < 10 ? 'inactive' : 'minimised';
      };
      const checkTop = () => {
        if (window.scrollY < 10) release();
      };
      const observer = new MutationObserver(() => {
        if (headerComponent.dataset.stickyState !== 'minimised') {
          headerComponent.dataset.stickyState = 'minimised';
          headerComponent.dataset.scrollDirection = 'none';
        }
      });
      observer.observe(headerComponent, { attributes: true, attributeFilter: ['data-sticky-state'] });
      headerComponent.dataset.stickyState = 'minimised';
      headerComponent.dataset.scrollDirection = 'none';
      document.addEventListener('scroll', checkTop);
      setTimeout(release, 5000);
      this.#headerLockRelease = release;
    }
  }

  /**
   * Handle browser back/forward
   */
  #handlePopState = () => {
    this.#fetchProducts({ updateUrl: false, skipRedirect: true });
  };

  /**
   * Handle media query changes
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    if (event.matches) {
      const targetElement = this.querySelector('[data-grid-layout="desktop-default-option"]');
      if (targetElement instanceof HTMLInputElement) {
        targetElement.checked = true;
      }
      this.#setLayout('default');
    } else {
      this.#setLayout('two');
      this.#syncToggleIcons('two');
    }
  };

  /**
   * Handle desktop grid density control changes from anywhere in the document (e.g. filter bar section).
   * The theme's on:change uses element.closest(), so controls outside this component never reach updateLayout.
   * @param {Event} event
   */
  #handleGridDensityChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.type !== 'radio') return;
    if (target.name !== 'grid') return;
    this.updateLayout({ target });
  };

  /**
   * Handle mobile layout toggle button clicks from anywhere in the document.
   * Only acts when the button is outside this component (inside-component clicks are handled by on:click delegation).
   * @param {Event} event
   */
  #handleGridToggleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const toggle = target.closest('.column-toggle');
    if (!toggle) return;
    if (this.contains(toggle)) return;
    this.handleLayoutToggle();
  };

  /**
   * Dispatch custom event with results data
   * @param {SearchspringFormattedResponse} response - Formatted response
   */
  #dispatchResultsEvent(response) {
    this.dispatchEvent(
      new CustomEvent('ss:results-loaded', {
        bubbles: true,
        detail: {
          products: response.products,
          pagination: response.pagination,
          facets: response.facets,
          sorting: response.sorting,
        },
      }),
    );
  }

  /**
   * Clear the product grid while new results are loading.
   * Called synchronously on page/filter change so stale cards are removed immediately.
   */
  #renderSkeletons() {
    const { grid } = this.refs;
    if (!grid) return;

    grid.innerHTML = '';
  }

  /**
   * Prefetch a page in the background to warm both the Searchspring API cache
   * and the batch card cache. Called on pagination link hover so the data is
   * ready by the time the user clicks.
   * @param {number} page - Page number to prefetch
   */
  async prefetchPage(page) {
    if (!this.isEnabled || !SearchspringAPI) return;

    // Cancel any in-flight prefetch for a different page
    if (this.#prefetchController) {
      this.#prefetchController.abort();
    }
    this.#prefetchController = new AbortController();
    const { signal } = this.#prefetchController;

    try {
      const searchOptions = {
        page,
        resultsPerPage: this.config.resultsPerPage,
        filters: this.activeFilters,
        sort: this.currentSort,
        ...(this.isSearchMode
          ? { query: this.searchQuery }
          : { collectionHandle: this.collectionHandle }),
      };

      const response = await SearchspringAPI.search(searchOptions);
      if (signal.aborted || !response) return;

      const formattedResponse = SearchspringAPI.formatSearchResponse(response);
      if (!formattedResponse?.products?.length) return;

      // Warm the batch card cache so the subsequent click render is fully instant
      const handles = formattedResponse.products.map((p) => p.handle).filter(Boolean);
      await this.#fetchBatchCards(handles, signal);
    } catch {
      // Prefetch errors are non-critical — silently ignore
    }
  }

  /**
   * Handle page change
   * @param {number} page - Page number
   */
  goToPage(page) {
    const url = new URL(window.location.href);
    if (page === 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', page.toString());
    }
    history.pushState({}, '', url.toString());
    this.#renderSkeletons();
    this.#fetchProducts({ skipRedirect: true });
  }

  /**
   * Handle sort change
   * @param {string} sortKey - Sort key (e.g., "price-asc")
   */
  setSort(sortKey) {
    const url = new URL(window.location.href);
    if (sortKey) {
      url.searchParams.set('sort_by', sortKey);
    } else {
      url.searchParams.delete('sort_by');
    }
    url.searchParams.delete('page'); // Reset to page 1

    history.pushState({}, '', url.toString());
    this.#fetchProducts({ skipRedirect: true });
  }

  /**
   * Updates the layout from a desktop radio input change.
   * @param {{ target: EventTarget | null }} event
   */
  updateLayout({ target }) {
    if (!target || !(target instanceof HTMLInputElement)) return;
    this.#setLayout(target.value);
  }

  /**
   * Toggles the mobile layout between one and two columns.
   */
  handleLayoutToggle() {
    const { grid } = this.refs;
    if (!grid) return;

    const current = grid.getAttribute('product-grid-view') || 'two';
    const next = current === 'one' ? 'two' : 'one';

    this.#setLayout(next);
    this.#syncToggleIcons(next);

    localStorage.setItem('grid-layout', next);
  }

  /**
   * Syncs the .is-active class on toggle icon spans to reflect the current layout.
   * @param {string} value - 'one' or 'two'
   */
  #syncToggleIcons(value) {
    document
      .querySelectorAll('.column-toggle__icon--one, .column-toggle__icon--two')
      .forEach((icon) => {
        icon.classList.toggle(
          'is-active',
          icon.classList.contains(`column-toggle__icon--${value}`),
        );
      });
  }

  /**
   * Sets the product-grid-view attribute on the grid element.
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Clear the API-level cache (useful for forced refresh)
   */
  clearCache() {
    if (typeof SearchspringAPI !== 'undefined') {
      SearchspringAPI.clearCache();
    }
  }
}

// Warm caches when the user hovers over a pagination link.
// pointerenter does not bubble, so we listen in the capture phase to delegate it.
document.addEventListener(
  'pointerenter',
  (e) => {
    const target = /** @type {Element} */ (e.target);
    const pageLink = target.closest?.('[data-ss-page]');
    if (!pageLink) return;

    const page = parseInt(/** @type {HTMLElement} */ (pageLink).dataset.ssPage ?? '', 10);
    const resultsList = pageLink.closest('searchspring-results-list');
    if (resultsList && page) {
      /** @type {SearchspringResultsList} */ (resultsList).prefetchPage(page);
    }
  },
  { capture: true },
);

// Register click handler for pagination links
document.addEventListener('click', (e) => {
  const target = /** @type {Element} */ (e.target);
  const pageLink = target.closest?.('[data-ss-page]');
  if (pageLink) {
    e.preventDefault();
    const page = parseInt(/** @type {HTMLElement} */ (pageLink).dataset.ssPage ?? '', 10);
    const resultsList = pageLink.closest('searchspring-results-list');
    if (resultsList && page) {
      /** @type {SearchspringResultsList} */ (resultsList).goToPage(page);
    }
  }

  // Handle clear filters button
  const clearButton = target.closest?.('[data-ss-clear-filters]');
  if (clearButton) {
    const url = new URL(window.location.href);
    // Remove all filter parameters
    [...url.searchParams.keys()].forEach((key) => {
      if (key.startsWith('filter.')) {
        url.searchParams.delete(key);
      }
    });
    url.searchParams.delete('page');
    history.pushState({}, '', url.toString());

    const resultsList = clearButton.closest('searchspring-results-list');
    if (resultsList) {
      /** @type {SearchspringResultsList} */ (resultsList).clearCache();
      resultsList.dispatchEvent(new CustomEvent(ThemeEvents.FilterUpdate));
    }
  }
});

if (!customElements.get('searchspring-results-list')) {
  customElements.define('searchspring-results-list', SearchspringResultsList);
}

export { SearchspringResultsList };
